export const FIAT_CHECKOUT_MODE_STORAGE_KEY = 'fiat-deposit-checkout-mode';

/** iframe load 后探测是否像被 X-Frame-Options 拦截 */
export const IFRAME_PROBE_DELAY_MS = 2000;

/** 长时间无回跳则自动 fallback 到小窗 */
export const IFRAME_FALLBACK_TIMEOUT_MS = 12000;

export const ONRAMP_POPUP_NAME = 'deepsage_alchemy_onramp';

/** MixPay 收银台弹窗名；与 Alchemy 隔离，避免同名窗互相覆盖 */
export const MIXPAY_ONRAMP_POPUP_NAME = 'deepsage_mixpay_onramp';

/** H5 整页跳转 Alchemy 前写入，回跳后在账户页读取 */
export const ONRAMP_PENDING_STORAGE_KEY = 'deepsage:onramp-pending';

/** MixPay 整页回跳 pending；与法币 key 隔离，避免互相覆盖 */
export const MIXPAY_ONRAMP_PENDING_STORAGE_KEY = 'deepsage:mixpay-onramp-pending';

/** TopPay 整页回跳 pending；与 Alchemy/MixPay 隔离 */
export const TOPPAY_ONRAMP_PENDING_STORAGE_KEY = 'deepsage:toppay-onramp-pending';

/**
 * @param {{ orderId?: string, amount?: number, currency?: string, funnelSessionId?: string, channel?: string }} payload
 * channel: 'fiat' | 'mixpay' | 'toppay'
 */
export function persistOnrampPending(payload = {}) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const channel =
      String(payload.channel ?? 'fiat')
        .trim()
        .toLowerCase() || 'fiat';
    const key =
      channel === 'mixpay'
        ? MIXPAY_ONRAMP_PENDING_STORAGE_KEY
        : channel === 'toppay'
        ? TOPPAY_ONRAMP_PENDING_STORAGE_KEY
        : ONRAMP_PENDING_STORAGE_KEY;
    const normalizedChannel =
      channel === 'mixpay' ? 'mixpay' : channel === 'toppay' ? 'toppay' : 'fiat';
    sessionStorage.setItem(
      key,
      JSON.stringify({
        orderId: String(payload.orderId ?? '').trim(),
        amount: Number(payload.amount) || 0,
        currency: String(payload.currency ?? '').trim(),
        funnelSessionId: String(payload.funnelSessionId ?? '').trim(),
        channel: normalizedChannel,
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / privacy mode
  }
}

/** @returns {{ orderId: string, amount: number, currency: string, funnelSessionId: string, channel: string, at: number } | null} */
export function readOnrampPending() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    // MixPay 优先：避免法币 pending 挡住刚支付完的 MixPay 回跳
    for (const key of [
      TOPPAY_ONRAMP_PENDING_STORAGE_KEY,
      MIXPAY_ONRAMP_PENDING_STORAGE_KEY,
      ONRAMP_PENDING_STORAGE_KEY,
    ]) {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') continue;
      const channel = String(
        parsed.channel ??
          (key === MIXPAY_ONRAMP_PENDING_STORAGE_KEY
            ? 'mixpay'
            : key === TOPPAY_ONRAMP_PENDING_STORAGE_KEY
            ? 'toppay'
            : 'fiat')
      )
        .trim()
        .toLowerCase();
      const normalizedChannel =
        channel === 'mixpay' ? 'mixpay' : channel === 'toppay' ? 'toppay' : 'fiat';
      return {
        orderId: String(parsed.orderId ?? '').trim(),
        amount: Number(parsed.amount) || 0,
        currency: String(parsed.currency ?? '').trim(),
        funnelSessionId: String(parsed.funnelSessionId ?? '').trim(),
        channel: normalizedChannel,
        at: Number(parsed.at) || 0,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearOnrampPending(channel) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const ch = String(channel ?? '')
      .trim()
      .toLowerCase();
    if (ch === 'mixpay') {
      sessionStorage.removeItem(MIXPAY_ONRAMP_PENDING_STORAGE_KEY);
      return;
    }
    if (ch === 'toppay') {
      sessionStorage.removeItem(TOPPAY_ONRAMP_PENDING_STORAGE_KEY);
      return;
    }
    if (ch === 'fiat') {
      sessionStorage.removeItem(ONRAMP_PENDING_STORAGE_KEY);
      return;
    }
    sessionStorage.removeItem(TOPPAY_ONRAMP_PENDING_STORAGE_KEY);
    sessionStorage.removeItem(MIXPAY_ONRAMP_PENDING_STORAGE_KEY);
    sessionStorage.removeItem(ONRAMP_PENDING_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** H5：整页跳转至 Alchemy 收银台（Standard API Step 3） */
export function redirectToOnrampCheckout(url) {
  const targetUrl = String(url ?? '').trim();
  if (!targetUrl || typeof window === 'undefined') return false;
  window.location.assign(targetUrl);
  return true;
}

export function readPreferredCheckoutMode() {
  if (typeof sessionStorage === 'undefined') return 'iframe';
  try {
    const mode = String(sessionStorage.getItem(FIAT_CHECKOUT_MODE_STORAGE_KEY) ?? '')
      .trim()
      .toLowerCase();
    return mode === 'popup' ? 'popup' : 'iframe';
  } catch {
    return 'iframe';
  }
}

export function persistCheckoutModePreference(mode) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (mode === 'popup') {
      sessionStorage.setItem(FIAT_CHECKOUT_MODE_STORAGE_KEY, 'popup');
      return;
    }
    sessionStorage.removeItem(FIAT_CHECKOUT_MODE_STORAGE_KEY);
  } catch {
    // ignore quota / privacy mode
  }
}

/** 登出：清法币结账挂起态与结账模式偏好 */
export function clearFiatCheckoutSessionStorage() {
  clearOnrampPending();
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(FIAT_CHECKOUT_MODE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * 启发式判断 iframe 是否可能被拒绝嵌入。
 * SecurityError（跨域）视为正常加载；可访问的 about:blank 空文档视为可能被拦。
 * @param {HTMLIFrameElement | null | undefined} iframeEl
 * @returns {boolean}
 */
export function isIframeLikelyBlocked(iframeEl) {
  if (!iframeEl) return false;
  try {
    const doc = iframeEl.contentDocument;
    if (!doc) return false;

    let href = '';
    try {
      href = String(iframeEl.contentWindow?.location?.href ?? '');
    } catch {
      return false;
    }

    if (href && href !== 'about:blank') return false;

    const bodyText = String(doc.body?.innerText ?? '').trim();
    const bodyHtml = String(doc.body?.innerHTML ?? '').trim();
    return !bodyText && !bodyHtml;
  } catch {
    return false;
  }
}

export const ONRAMP_POPUP_FEATURES = 'width=420,height=720,scrollbars=yes,resizable=yes';

/**
 * 在用户点击同步阶段打开空窗，避免 await 订单后 window.open 被拦截。
 * @param {string} [popupName]
 * @returns {Window | null}
 */
export function openOnrampCheckoutBlank(popupName = ONRAMP_POPUP_NAME) {
  if (typeof window === 'undefined') return null;
  try {
    return window.open('about:blank', popupName || ONRAMP_POPUP_NAME, ONRAMP_POPUP_FEATURES);
  } catch {
    return null;
  }
}

/**
 * 同步打开空标签页（不带 window features，避免被当成小窗）。
 * MixPay 等收银台用：PC / H5 统一新标签。
 * @param {string} [tabName]
 * @returns {Window | null}
 */
export function openOnrampCheckoutBlankTab(tabName = '_blank') {
  if (typeof window === 'undefined') return null;
  try {
    return window.open('about:blank', tabName || '_blank');
  } catch {
    return null;
  }
}

/**
 * @param {string} url
 * @param {string} [popupName]
 * @returns {Window | null}
 */
export function openOnrampCheckoutPopup(url, popupName = ONRAMP_POPUP_NAME) {
  if (typeof window === 'undefined') return null;
  const targetUrl = String(url ?? '').trim();
  if (!targetUrl) return null;

  try {
    return window.open(targetUrl, popupName || ONRAMP_POPUP_NAME, ONRAMP_POPUP_FEATURES);
  } catch {
    return null;
  }
}

/**
 * 在新标签页打开收银台 URL（不带 window features）。
 * @param {string} url
 * @param {string} [tabName]
 * @returns {Window | null}
 */
export function openOnrampCheckoutTab(url, tabName = '_blank') {
  if (typeof window === 'undefined') return null;
  const targetUrl = String(url ?? '').trim();
  if (!targetUrl) return null;
  try {
    return window.open(targetUrl, tabName || '_blank');
  } catch {
    return null;
  }
}
